import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMcpAnnouncement } from '@/hooks/use-mcp-announcement';

interface McpAnnouncementProps {
  /** Another dialog has the screen; wait for it rather than stack on it. */
  suppressed?: boolean;
}

const FEATURES = [
  {
    key: 'ask',
    icon: 'ti-message-chatbot',
    tile: 'bg-blue-50',
    tone: 'text-blue-600',
  },
  {
    key: 'write',
    icon: 'ti-file-text',
    tile: 'bg-purple-50',
    tone: 'text-purple-600',
  },
  {
    key: 'safe',
    icon: 'ti-shield-check',
    tile: 'bg-green-50',
    tone: 'text-green-600',
  },
] as const;

/**
 * A "What's new" dialog telling someone who already uses ViewMetrics that they
 * can connect an AI assistant, once. A corner card was tried first and was too
 * easy to miss for a feature nothing else in the product mentions.
 *
 * Every way out — "Not now", the close button, Escape, clicking away — ends it
 * for good on this device. The setup steps stay on Settings → Connected apps,
 * so there is nothing to gain by asking twice.
 *
 * Who sees it, and when, is useMcpAnnouncement's job.
 */
const McpAnnouncement: FC<McpAnnouncementProps> = ({ suppressed = false }) => {
  const { t } = useTranslation('connectedApps');
  const navigate = useNavigate();
  const { visible, setupPath, accept, dismiss } = useMcpAnnouncement({
    suppressed,
  });

  if (!visible) return null;

  return (
    <Dialog open onOpenChange={open => !open && dismiss()}>
      <DialogContent className="sm:max-w-lg" data-testid="mcp-announcement">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold uppercase text-green-700">
              {t('announcement_new')}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('announcement_eyebrow')}
            </span>
          </div>
          <DialogTitle className="text-2xl">
            {t('announcement_title')}
          </DialogTitle>
          <DialogDescription className="pt-2 text-base">
            {t('announcement_body')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {FEATURES.map(feature => (
            <div key={feature.key} className="flex items-start gap-3">
              <div
                className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${feature.tile}`}
              >
                <i className={`ti ${feature.icon} text-lg ${feature.tone}`} />
              </div>
              <div>
                <h4 className="text-sm font-medium">
                  {t(`announcement_${feature.key}_title`)}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t(`announcement_${feature.key}_body`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={dismiss}
            className="w-full sm:w-auto"
          >
            {t('announcement_dismiss')}
          </Button>
          <Button
            onClick={() => {
              accept();
              navigate(setupPath);
            }}
            className="w-full sm:w-auto"
          >
            {t('announcement_accept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default McpAnnouncement;
